import { Settings, X, Minimize2, Maximize2, AlertTriangle } from 'lucide-react'
import { QUANTUM_COLORS } from './constants.js'
import { formatPriceWithPrecision } from '../../utils/pricePrecision.js'

function pctFromEntry(entry, price) {
  if (!Number.isFinite(entry) || entry === 0 || !Number.isFinite(price)) return null
  return ((price - entry) / entry) * 100
}

// Formato es-ES (coma decimal, un decimal fijo) — Marc lo pidió literal en
// los ejemplos de V4 ("-9,0%", "+19,0%"), no el "." que usaba `toFixed`.
function formatPctEs(v, { forceSign = true } = {}) {
  if (!Number.isFinite(v)) return '—'
  const sign = forceSign && v >= 0 ? '+' : ''
  return `${sign}${v.toFixed(1).replace('.', ',')}%`
}

function formatShortDate(unixSeconds) {
  if (!Number.isFinite(unixSeconds)) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
}

// B4 (validación en pantalla de Marc, 2026-08-15): "TP254959.3" — label y
// valor pegados sin espacio. `justify-between` sin `gap` no garantiza NINGÚN
// hueco mínimo: si el ancho intrínseco de label+valor iguala o supera el de
// la columna (grid-cols-2 de ~90px, valores largos como un precio con
// decimales + porcentaje), se quedan pegados en vez de separarse — la misma
// familia de bug que B2 (columna estrecha + contenido largo), pero en el
// hueco ENTRE columnas, no dentro de una. `gap-2` fuerza un mínimo de 8px
// entre label y valor en TODAS las filas, largas o cortas.
function Row({ label, value, color, title }) {
  return (
    <div className="flex justify-between gap-2" title={title}>
      <span className="text-[9px] shrink-0" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{label}</span>
      <span className="text-[9px] font-medium text-right" style={{ color: color || 'var(--ink, #15211B)' }}>{value}</span>
    </div>
  )
}

export default function RangoQuantumBadge({
  result,
  config,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0',
  onHeaderMouseDown,
  botDivergence = null,
}) {
  const stats = result?.stats ?? {
    closedCount: 0, openCount: 0, tp1Rate: 0, tp2Rate: 0, liquidationRate: 0, slRate: 0, timeStopRate: 0,
    winrate: 0, expectancyPctMargin: 0, recommendation: [],
  }
  const active = result?.activeSignal
  const last10 = result?.showLastSignals !== false ? (stats.last10 ?? []) : []

  // B3 (validación en pantalla de Marc, 2026-08-15): precisión REAL del
  // activo (result.pricePrecision, calculada en index.js con las mismas
  // velas que ve el eje del chart), no una regla de umbrales fija — evita
  // el desajuste "69610.95" en el eje vs "69611.0" en el badge.
  const formatPrice = (v) => formatPriceWithPrecision(v, result?.pricePrecision)
  const isBull = active?.direction === 'long'
  const activeColor = active ? (isBull ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear) : null
  const leverage = config?.leverage ?? 5
  // % de la posición que cierra en TP1/TP2 — configurable (`tp1WeightPct`),
  // NUNCA hardcodeado 60/40 (bug pre-existente arreglado de paso al
  // reescribir esta fila para V4: el texto decía "TP1 (60%)" fijo aunque el
  // usuario hubiera cambiado el reparto en el panel).
  const tp1WeightPct = config?.tp1WeightPct ?? 60
  const tp2WeightPct = 100 - tp1WeightPct

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[210px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          Rango Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && active && (
            <span className="ml-1.5 text-[10px]" style={{ color: activeColor }}>· {isBull ? 'LONG' : 'SHORT'}</span>
          )}
          {minimized && botDivergence && (
            <span
              className="ml-1 inline-flex items-center"
              title={`Esta config no es la de tu bot (${botDivergence.botName}):\n· ${botDivergence.diffs.join('\n· ')}`}
            >
              <AlertTriangle size={11} style={{ color: 'var(--gold, #B08D57)' }} />
            </span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        )}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          {/* WP H-G: la config del chart diverge de la de un bot vivo del
              usuario (mismo indicador+símbolo+TF) — lo pintado no es lo que
              el bot opera. Lo calcula la página (botDivergence.js) con la
              lista de bots ya cargada; sin endpoint nuevo. */}
          {botDivergence && (
            <div
              className="px-2.5 py-1.5 flex items-start gap-1.5"
              style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))', background: 'rgba(176,141,87,0.12)' }}
              title={botDivergence.diffs.join('\n')}
            >
              <AlertTriangle size={11} className="mt-0.5 shrink-0" style={{ color: 'var(--gold, #B08D57)' }} />
              <span className="text-[9px] leading-tight" style={{ color: 'var(--ink, #15211B)' }}>
                Esta config no es la de tu bot ({botDivergence.botName}): {botDivergence.diffs.join(' · ')}
              </span>
            </div>
          )}

          <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
            <div className="pt-1.5 pb-1 flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>
                {active ? 'Señal activa' : 'Sin señal activa'}
              </span>
              {active && (
                <span className="text-[10px] font-medium" style={{ color: activeColor }}>
                  {isBull ? 'LONG' : 'SHORT'} @ {formatPrice(active.entryPrice)}
                </span>
              )}
            </div>
            {/* Verificación pre-merge de Marc, 2026-08-15: "1 abierta desde 17
                may" en BTC/USDT 1h se leyó como si la señal activa llevara
                abierta desde esa fecha (~90d), cuando en realidad esa fecha
                es el inicio de la ventana de estadísticas (SIGNAL_WINDOW_DAYS
                = 90 en stats.js, ajena a la propia señal abierta) y la señal
                real llevaba solo 11 días — el time-stop SÍ se aplica en vivo
                (comprobado con reproducción directa contra datos reales de la
                API y con el "zombi" de BTC/4h, que con time-stop=15
                desaparece). El bug era de legibilidad, no de lógica: sin la
                fecha/antigüedad de la señal activa a la vista, el lector solo
                tenía la fecha de la ventana de cierre para asociar, y las
                asociaba mal. Mostrar aquí, en su propia sección, la fecha y
                antigüedad reales de la señal activa lo hace autocontenido.
                Empieza con "Entrada", no "abierta": un primer intento con
                "abierta {fecha}" justo debajo del precio de entrada (que
                termina en dígitos, p.ej. "524.15") colisionaba con el regex
                preexistente de V1 (`/(\d+)\s+abiertas?/`, busca el contador
                "N abierta(s)" del pie de Estadísticas) — capturaba los
                dígitos finales del precio como si fueran ese contador. */}
            {active && Number.isFinite(active.time) && (
              <div className="pb-1 text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                Entrada {formatShortDate(active.time)} · hace {Math.max(0, Math.floor((Date.now() / 1000 - active.time) / 86400))}d
                {config?.timeStopDays > 0 ? ` (time-stop ${config.timeStopDays}d)` : ''}
              </div>
            )}
            {active && (() => {
              const tp1Pct = pctFromEntry(active.entryPrice, active.tp1)
              const tp2Pct = pctFromEntry(active.entryPrice, active.tp2)
              const stopPrice = active.slPrice ?? active.liqPrice
              const stopPct = pctFromEntry(active.entryPrice, stopPrice)
              const stopLabel = active.slPrice != null ? 'SL' : 'Liq.'
              const marginTitle = (pct) => Number.isFinite(pct)
                ? `A ${leverage}x, impacto en margen ≈ ${formatPctEs(pct * leverage, { forceSign: false })}`
                : undefined
              return (
                // V4 (validación en pantalla de Marc, 2026-08-15): "lenguaje
                // claro" — cada nivel es una frase completa ("TP1 {precio} ·
                // cierra 60% · {pct} desde entrada"), no una fila de
                // etiqueta/valor de dos columnas. De paso resuelve B4 (label
                // pegado al valor): al pasar de grid-cols-2 a una lista de
                // ancho completo, ya no hay una columna estrecha de ~90px
                // donde precio+porcentaje puedan quedarse sin hueco respecto
                // a su label. El % es siempre de PRECIO (nunca de margen) —
                // el tooltip (hover) da el equivalente en margen al
                // apalancamiento configurado, sin ocupar espacio fijo.
                <div className="flex flex-col pb-1">
                  <div className="text-[9px] leading-snug py-0.5" title={marginTitle(tp1Pct)}>
                    <span className="font-medium" style={{ color: QUANTUM_COLORS.bull }}>TP1 {formatPrice(active.tp1)}</span>
                    <span style={{ color: 'var(--ink-soft, #4A5A52)' }}> · cierra {tp1WeightPct}% · {formatPctEs(tp1Pct)} desde entrada</span>
                  </div>
                  <div className="text-[9px] leading-snug py-0.5" title={marginTitle(tp2Pct)}>
                    <span className="font-medium" style={{ color: QUANTUM_COLORS.bull }}>TP2 {formatPrice(active.tp2)}</span>
                    <span style={{ color: 'var(--ink-soft, #4A5A52)' }}> · cierra {tp2WeightPct}% · {formatPctEs(tp2Pct)} desde entrada</span>
                  </div>
                  <div className="text-[9px] leading-snug py-0.5" title={marginTitle(Number.isFinite(stopPct) ? Math.abs(stopPct) : stopPct)}>
                    <span className="font-medium" style={{ color: QUANTUM_COLORS.bear }}>{stopLabel} {formatPrice(stopPrice)}</span>
                    <span style={{ color: 'var(--ink-soft, #4A5A52)' }}> · {formatPctEs(Number.isFinite(stopPct) ? Math.abs(stopPct) : stopPct)} en contra</span>
                  </div>
                  {active.tp1Hit && (
                    <div className="text-[9px] pt-0.5" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                      TP1 alcanzado — {tp2WeightPct}% sigue a TP2
                    </div>
                  )}
                </div>
              )
            })()}
          </div>

          {stats.recommendation?.length > 0 && (
            <div className="px-2.5 py-1.5 flex flex-col gap-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))', background: 'rgba(176,141,87,0.08)' }}>
              {stats.recommendation.map((notice, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <AlertTriangle size={11} className="mt-0.5 shrink-0" style={{ color: 'var(--gold, #B08D57)' }} />
                  <span className="text-[9px] leading-tight" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{notice}</span>
                </div>
              ))}
            </div>
          )}

          <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
            <div className="pt-1.5 flex items-baseline justify-between gap-2">
              <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>
                Estadísticas · 90d por cierre
              </span>
              {stats.openCount > 0 && (
                <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{stats.openCount} abierta{stats.openCount === 1 ? '' : 's'}</span>
              )}
            </div>
            {/* V3 (validación en pantalla de Marc, 2026-08-15): la ventana
                cuenta por FECHA DE CIERRE, no de entrada (una señal puede
                abrir fuera de los 90d y contar igual si cerró dentro) —
                hecho visible aquí para que no quede como un detalle de
                implementación invisible al usuario. */}
            <div className="pb-1 text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
              desde {formatShortDate(stats.windowStart)} · {stats.closedCount} cerrada{stats.closedCount === 1 ? '' : 's'}
            </div>
            {stats.closedCount === 0 ? (
              // B5 (validación en pantalla de Marc, 2026-08-15): 0% en
              // Winrate/TP1/TP2/Liquidación/SL con 0 señales cerradas se lee
              // como "todo perdido" — es en realidad "no hay datos todavía".
              // "—" en cada métrica distingue explícitamente ambos casos.
              <div className="pb-1 text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                Sin señales cerradas en la ventana
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 pb-1">
                <Row label="Winrate" value={`${(stats.winrate * 100).toFixed(0)}%`} />
                <Row label="Expectancy" value={`${stats.expectancyPctMargin >= 0 ? '+' : ''}${stats.expectancyPctMargin.toFixed(1)}%`} color={stats.expectancyPctMargin >= 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear} />
                <Row label="TP1" value={`${(stats.tp1Rate * 100).toFixed(0)}%`} color={QUANTUM_COLORS.bull} />
                <Row label="TP2" value={`${(stats.tp2Rate * 100).toFixed(0)}%`} color={QUANTUM_COLORS.bull} />
                <Row label="Liquidación" value={`${(stats.liquidationRate * 100).toFixed(0)}%`} color={QUANTUM_COLORS.bear} />
                <Row label="SL" value={`${(stats.slRate * 100).toFixed(0)}%`} color={QUANTUM_COLORS.bear} />
                <Row
                  label="TS"
                  value={`${(stats.timeStopRate * 100).toFixed(0)}%`}
                  color={QUANTUM_COLORS.neutral}
                  title={`Cierres por time-stop (${config?.timeStopDays ?? 15}d)`}
                />
              </div>
            )}
          </div>

          {last10.length > 0 && (
            <div className="px-2.5 pb-2" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1.5 pb-1">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>Últimas señales</span>
              </div>
              <div className="flex flex-col gap-0.5 max-h-24 overflow-y-auto">
                {[...last10].reverse().slice(0, 10).map((s, i) => (
                  <div key={`${s.time}-${i}`} className="flex justify-between gap-2 text-[9px]">
                    <span style={{ color: s.direction === 'long' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear }}>
                      {s.direction === 'long' ? 'LONG' : 'SHORT'} {formatPrice(s.entryPrice)}
                    </span>
                    <span style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                      {s.outcome === 'active' ? 'Abierta' : s.outcome === 'tp2' ? 'TP2' : s.outcome === 'liquidated' ? 'Liq.' : s.outcome === 'sl' ? 'SL' : s.outcome === 'time_stop' ? 'TS' : s.tp1Hit ? 'TP1' : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
